import React, { useState, useEffect } from 'react';
import './SettingsPage.css';
import { LANGUAGES, useLanguage } from './i18n/LanguageContext';

const applyThemeColor = (isDark) => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', isDark ? '#121212' : '#764CA2');
};

const SettingsPage = ({ onBack }) => {
  const { t, language, setLanguage } = useLanguage();
  const [notifications, setNotifications] = useState(true);
  const [locationAccess, setLocationAccess] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.body.classList.remove('dark-mode');
      localStorage.setItem('darkMode', 'false');
    }
    applyThemeColor(darkMode);
  }, [darkMode]);

  useEffect(() => {
    let permissionStatus;
    let active = true;

    const sync = () => {
      if (!active || !permissionStatus) return;
      setLocationAccess(permissionStatus.state === 'granted');
    };

    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        permissionStatus = status;
        sync();
        permissionStatus.onchange = sync;
      }).catch(() => {
        if (active) setLocationAccess(false);
      });
    }

    return () => {
      active = false;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  const handleLocationToggle = () => {
    if (locationAccess || !navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => setLocationAccess(true),
      () => setLocationAccess(false)
    );
  };

  const currentLanguage = LANGUAGES.find((item) => item.code === language) || LANGUAGES[0];

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button className="settings-back-btn" onClick={onBack} aria-label={t('Go back')}>
          <img src={process.env.PUBLIC_URL + '/images/back_button.svg'} alt="" />
        </button>
        <div className="settings-heading">
          <h1 className="settings-title">{t('Settings')}</h1>
          <p className="settings-subtitle">{t('App preferences & legal')}</p>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-card">
          <div className="settings-item">
            <div className="item-left">
              <div className="item-icon-bg">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6981 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="item-label">{t('Notifications')}</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notifications}
                onChange={() => setNotifications(!notifications)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="settings-item">
            <div className="item-left">
              <div className="item-icon-bg">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="item-label">{t('Location Access')}</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={locationAccess}
                onChange={handleLocationToggle}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <button
            type="button"
            className="settings-item"
            onClick={() => setLanguageOpen((open) => !open)}
          >
            <div className="item-left">
              <div className="item-icon-bg">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M3 12H21M12 3C14.5 5.5 15.5 8.5 15.5 12C15.5 15.5 14.5 18.5 12 21C9.5 18.5 8.5 15.5 8.5 12C8.5 8.5 9.5 5.5 12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="item-label">{t('Language')}</span>
            </div>
            <span className="item-value">{currentLanguage.short} <span aria-hidden="true">&gt;</span></span>
          </button>
          {languageOpen && (
            <div className="language-options">
              {LANGUAGES.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  className={`language-option${item.code === language ? ' is-active' : ''}`}
                  onClick={() => {
                    setLanguage(item.code);
                    setLanguageOpen(false);
                  }}
                >
                  {item.native}
                </button>
              ))}
            </div>
          )}

          <div className="settings-item">
            <div className="item-left">
              <div className="item-icon-bg">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="item-label">{t('Dark Mode')}</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-card">
          <button className="settings-action-item">
            <span className="item-label">{t('Privacy Policy')}</span>
            <svg className="chevron-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="settings-action-item">
            <span className="item-label">{t('Terms of Service')}</span>
            <svg className="chevron-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="settings-action-item no-click">
            <span className="item-label">{t('About Moviq')}</span>
            <span className="version-text">v1.0.2</span>
          </div>
        </div>

        <div className="settings-footer">
          <p>{t('Made with ❤ for Travellers').split('❤')[0]}<span className="heart">❤</span>{t('Made with ❤ for Travellers').split('❤')[1]}</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
